import i18n from '../i18n';
import Big from 'big.js';
import * as models from 'smg/admin/models';
import { ConfirmPurchaseOrderDuplicates, FindPurchaseOrderDuplicates } from 'smg/admin/purchase-order-duplicates';
import axios from 'smg/axios';
import { ShowError } from 'smg/errors';
import { ReportValidity } from 'smg/form';
import * as accounts from '../accounts';
import type { NewCustomerDraft } from './contact';
import { ChargeOrderPayment } from './orderpayments';
import type { ChargePaymentDeps, PaymentDecline } from './payment';

export interface PlaceOrderHandle {
    // Reflects current submit-readiness in the button. The page calls this
    // from contact's onCustomerChange so the button enables the moment we
    // have a resolved userID + realm, and disables again if the admin clears
    // them mid-flight.
    refresh(): void;
}

export interface PlaceOrderDeps {
    getCartID: () => string;
    getUserID: () => string;
    getCompanyID: () => string;
    // Current contact email as typed (trimmed/lowercased), non-empty even
    // when it didn't resolve to an existing user. A brand-new customer has
    // no userID — the button enables on email + realm instead, and the
    // server resolves-or-creates the user from the email at submit.
    getCustomerEmail: () => string;
    // E.164 contact phone for that created user ('' when empty). Async
    // because the phone rides intlTelInput's utils.js promise.
    getCustomerPhone: () => Promise<string>;
    // The new-customer name controls: reuse the shipping address's name &
    // company (the default) or the explicit first/last/company the admin
    // typed after untoggling the switch.
    getNewCustomerDraft: () => NewCustomerDraft;
    // Last shipping override the admin set in the summary pencil-dropdown,
    // or undefined if there isn't one. Mirrors what the GET /carts/:id
    // preview used to compute the displayed total — server applies the
    // same value to the persisted shipping line.
    getShippingOverride: () => InstanceType<typeof Big> | undefined;
    // Pending line item qty/price overrides from the summary's per-item
    // editor — the same values every GET /carts/:id preview fetch carried,
    // so the order the server assembles from the cart matches the previewed
    // totals. Empty when the admin made no edits.
    getLineItemOverrides: () => models.ICartLineItemOverride[];
    // Current shipping selection — either a picked existing ShippingOptionID
    // or the form-derived ShippingOption submodel. Undefined when
    // skip-shipping is on or the form is incomplete; the server will create
    // an order without an OrderShippingOption in that case. Async because
    // the recipient phone rides intlTelInput, which formats to E.164 via
    // a Promise.
    getOrderShippingOption: () => Promise<models.OrderShippingOption | undefined>;
    // Current tax-exemption selection — an existing pick (bare
    // userTaxExemptionID) or a newly-authored exemption (inline
    // userTaxExemption submodel with cert uploads). Undefined when there's
    // nothing applicable to attach (skip, incomplete form, or the exemption
    // doesn't match the shipping destination). The server's
    // UserTaxExemptionOrder.Normalize materializes the inline form and links
    // it to the order in the same transaction.
    getUserTaxExemptionOrder: () => Promise<models.UserTaxExemptionOrder | undefined>;
    // Empty when the admin picked "No" or hasn't filled the date yet. Format
    // is yyyy-mm-dd — matches the admin /orders endpoint's `eventDate`
    // `datetime=2006-01-02` binding so the string can ride the POST body as-is.
    getEventDate: () => string;
    // Whether the admin checked "Opt customer in to SMS updates & reminders".
    // Only `true` rides the POST body — leaving it unchecked is a no-op on
    // the server, which matches the UI callout that promises unchecked does
    // NOT opt the customer out.
    getSMSOptIn: () => boolean;
    // Same shape as getSMSOptIn but for the "Customer agrees designs can be
    // used for marketing" checkbox.
    getPromotionalApproval: () => boolean;
    // Trimmed contents of the "Notes & special requests" textarea. Non-empty
    // values post a Sales-addressed order event so the note lands in the
    // order timeline.
    getNotes: () => string;
    // Selected promotional code id, or empty when none is applied. The server
    // re-validates it (company/network, expiry, min subtotal) and appends the
    // discount as a line item — matching the discount the summary previewed via
    // GET /carts/:id ?promo=, so the persisted order total equals what the
    // admin saw and the payment charged.
    getPromotionalCode: () => string;
    // Deposit order ids the admin unchecked in the prepayment section. Rides
    // the POST as skipDepositOrderIDs so the server's deposit transfer leaves
    // those deposits live (payments and all) for a future order. Empty =
    // apply every available deposit (the default).
    getSkipDepositOrderIDs: () => string[];
    // The account-balance credit to apply first, before charging the payment
    // method. Read fresh on each (re)try so the admin can adjust it after a
    // balance decline. `applied` is already clamped to the available balance;
    // `useBalance` distinguishes a deliberate "No" from an unfilled "Yes".
    getBalanceDraft: () => { applied: InstanceType<typeof Big>; useBalance: boolean };
    // Waits until the checkout's pricing inputs and its preview have settled
    // together and returns what that preview priced; the order is created from
    // exactly that. undefined means the preview failed (the summary already
    // showed the error) and the submit must stop.
    settlePricing: () => Promise<PricedSnapshot | undefined>;
    // Freeze/thaw the payment section's cross-origin Spreedly card iframes.
    // Frozen at submit start so the admin can't edit a card mid-charge; thawed
    // on a pre-charge failure so they can correct the form and resubmit.
    freezePaymentIframes: () => void;
    thawPaymentIframes: () => void;
    // Charge the selected payment method (cards / manual / skip) against the
    // created order. Owns the per-card charge UI + decline/resume; reports the
    // outcome back through the callbacks so this handler drives the button.
    chargePayment: (deps: ChargePaymentDeps) => void;
    // Pause the background cart-change poll before the POST so a tick
    // can't reload the page mid-submit. Paired with startWatcher on the
    // failure path so an abandoned (failed) submit doesn't leave stale-cart
    // detection permanently disabled.
    stopWatcher: () => void;
    // Resume polling after a failed POST so the page stays current if the
    // admin sits on it before retrying. The success path doesn't need this
    // — it redirects to the new order's detail page.
    startWatcher: () => void;
    // PO numbers on the selected payment, read once the form is frozen.
    getPurchaseOrderNumbers: () => string[];
    // The selected realm's network. Empty when no realm is selected.
    getNetworkID: () => string;
}

// What the settled checkout priced for this submit: the customer and the
// available-discount choice its preview used. postOrder builds the customer
// and discount fields from it and nothing read later.
export interface PricedSnapshot {
    userID: string;
    availableDiscountChoice: InstanceType<typeof Big> | undefined;
}

// SetupPlaceOrder owns the submit → charge flow. Payments happen AFTER the
// order is created (and the cart consumed), so once POST /v2/orders succeeds we
// never re-submit — we charge the account balance first (if applied), then the
// selected payment method one source at a time. A card decline pauses the run:
// the declined card is re-enabled for correction and the button becomes "Retry
// charge", which resumes from that card. Mirrors the express customer checkout.
//
// userTaxExemptionOrders rides the same nested-CRUD path as
// orderShippingOptions: taxexemption.snapshot() returns either an
// existing-pick (bare userTaxExemptionID) or a newly-authored exemption
// (inline userTaxExemption submodel + cert uploads), and the server's
// UserTaxExemptionOrder.Normalize materializes whichever was supplied.
//
// The order shell covers cartID + userID (or email/phone, which the server
// resolves-or-creates a customer from) + shippingOverride +
// orderShippingOptions + userTaxExemptionOrders + eventDate + smsOptIn +
// promotionalApproval + notes + promotionalCodeID + skipDepositOrderIDs.
export function SetupPlaceOrder(deps: PlaceOrderDeps): PlaceOrderHandle {
    const $btn = document.getElementById('place-order-btn') as HTMLButtonElement;
    const $form = document.getElementById('checkout-form') as HTMLFormElement;
    const $declineAlert = document.getElementById('payment-decline-alert') as HTMLElement;
    const $declineMessage = $declineAlert.querySelector('.payment-decline-alert-message') as HTMLElement;
    const $declineOrderLink = $declineAlert.querySelector('.payment-decline-alert-order-link') as HTMLAnchorElement;
    const $declineOrderLinkText = $declineAlert.querySelector('.payment-decline-alert-order-link-text') as HTMLElement;

    let submitting = false;
    // Set after a decline; the button then drives the in-place retry rather
    // than a re-submit (the cart is already consumed — re-posting would fail).
    let retryAction: (() => void) | undefined;
    // True until the account balance has been charged (or skipped) for this
    // submit. Survives a card decline/resume so balance isn't re-charged on a
    // card retry, and resets at the start of each fresh submit.
    let balancePending = false;
    // One idempotency key for the whole balance charge attempt, reused across
    // retries so a lost response can't double-debit (#5656).
    let balanceIdemKey: string | undefined;
    // Controls disabled by disableForm so a pre-charge failure re-enables only
    // those (preserving payment.ts's own fieldset gating for inactive types).
    let disabledByMe: (HTMLInputElement | HTMLSelectElement | HTMLTextAreaElement | HTMLButtonElement)[] = [];
    // Upload dropzone labels + thumbnail containers neutralized (dimmed +
    // pointer-events) by disableForm — they aren't form controls, so `disabled`
    // can't reach them.
    let neutralizedByMe: HTMLElement[] = [];
    // Containers whose children async loads re-render (saved shipping and tax
    // exemption cards, summary item rows). inert on the container also covers
    // children rendered after the lock, which disabling individual controls
    // cannot. The saved payment cards are not listed: the payment module locks
    // them itself and unlocks them to correct a declined charge.
    const $rerenderedContainers = ['shipping-options-scroller', 'tax-exempt-options-scroller', 'summary-items'].
        map(id => document.getElementById(id)).
        filter(($el): $el is HTMLElement => $el !== null);
    let inertByMe: HTMLElement[] = [];

    function ready(): boolean {
        // userID OR email: an email that didn't resolve to an existing user
        // is still a placeable order — the server creates the customer from
        // it (validity is enforced by ReportValidity at submit, and again
        // server-side).
        return Boolean(deps.getCartID() && deps.getCompanyID() && (deps.getUserID() || deps.getCustomerEmail()));
    }

    function refresh() {
        if (submitting) {
            return;
        }
        $btn.disabled = !ready();
    }

    $btn.addEventListener('click', () => {
        if (retryAction) {
            retryAction();
            return;
        }
        if (submitting || !ready()) {
            return;
        }
        void submit();
    });

    // The network and PO numbers the duplicate check last cleared, as one key.
    // A decline unlocks the card PO field, so a retry asks again only when they
    // changed; recoverBeforeCreate drops it because the realm unlocks too.
    let clearedPurchaseOrders: string | undefined;

    // Resolves true when the current PO numbers have no duplicates or the admin
    // confirmed them. excludeOrderID leaves out the order a retry is charging.
    // It loops so the numbers it clears are still the ones on the form.
    async function checkPurchaseOrderNumbers(excludeOrderID?: string): Promise<boolean> {
        for (;;) {
            const networkID = deps.getNetworkID();
            const purchaseOrderNumbers = deps.getPurchaseOrderNumbers();
            const key = [networkID, ...purchaseOrderNumbers].join('\n');
            if (clearedPurchaseOrders === key) {
                return true;
            }
            setProcessing(i18n('checkingPONumber'));
            const duplicates = await FindPurchaseOrderDuplicates({ networkID, purchaseOrderNumbers, excludeOrderID });
            if (duplicates.length > 0 && !await ConfirmPurchaseOrderDuplicates(duplicates)) {
                return false;
            }
            clearedPurchaseOrders = key;
        }
    }

    // Re-checks the PO numbers before a charge retry; a cancel or a failed
    // lookup puts the decline state back instead of charging.
    function retryAfterPurchaseOrderCheck(order: models.Order, restore: () => void, retry: () => void): void {
        $btn.disabled = true;
        checkPurchaseOrderNumbers(order.id).then(confirmed => {
            if (confirmed) {
                retry();
            } else {
                restore();
            }
        }, (err: unknown) => {
            ShowError(err);
            restore();
        });
    }

    async function submit(): Promise<void> {
        // The form is `novalidate` and the button is `type="button"`, so
        // HTML5 won't auto-block submission on missing required fields. Run
        // ReportValidity ourselves so a half-filled form blocks here with the
        // usual .is-invalid feedback instead of slipping past — otherwise
        // snapshot() returns undefined for an incomplete address and the order
        // would be created with no OrderShippingOption at all, which looks
        // identical to a deliberate skip-shipping submit.
        if (!ReportValidity($form)) {
            return;
        }
        submitting = true;
        balancePending = true;
        retryAction = undefined;
        hideDecline();
        disableForm();
        deps.stopWatcher();
        // Cross-origin card iframes can't be reached by the native disable
        // above — freeze them for the whole submit window so the admin can't
        // edit a card while the charge sequence is mid-flight. The PO number
        // and realm are frozen with the form, so the duplicate check below
        // reads them once.
        deps.freezePaymentIframes();

        let confirmed: boolean;
        try {
            confirmed = await checkPurchaseOrderNumbers();
        } catch (err) {
            ShowError(err);
            recoverBeforeCreate();
            return;
        }
        if (!confirmed) {
            recoverBeforeCreate();
            return;
        }
        setProcessing('Placing order…');

        // Creating the order consumes the cart. Wait until the displayed
        // total is the preview of these inputs — the 500ms debounce, and a
        // lookup that can still force the discount to zero, would otherwise
        // let this POST outrun it.
        const priced = await deps.settlePricing();
        if (!priced) {
            recoverBeforeCreate();
            return;
        }

        let order: models.Order;
        try {
            order = await postOrder(priced);
        } catch (err) {
            ShowError(err);
            recoverBeforeCreate();
            return;
        }

        // Order created; the cart is consumed, so from here we never re-submit
        // — only resume the charge sequence.
        runCharges(order);
    }

    // The order wasn't created (cart still intact) — fully recover so the
    // admin can fix the input and submit again.
    function recoverBeforeCreate(): void {
        clearedPurchaseOrders = undefined;
        deps.thawPaymentIframes();
        reEnableForm();
        deps.startWatcher();
        submitting = false;
        retryAction = undefined;
        setLabel('Place order');
        refresh();
    }

    async function postOrder(priced: PricedSnapshot): Promise<models.Order> {
        // Snapshotted up front (not at its push below) because the
        // new-customer branch reads the shipping name/company off it.
        const orderShippingOption = await deps.getOrderShippingOption();

        const body: Record<string, unknown> = {
            'cartID': deps.getCartID(),
            'companyID': deps.getCompanyID(),
        };
        const userID = priced.userID;
        if (userID) {
            body['userID'] = userID;
        } else {
            // New-customer checkout: the server resolves the email to a user
            // in the realm's network, creating one (with this phone + name)
            // when none exists — including gracefully reusing a user created
            // between our autocomplete lookup and this submit.
            body['email'] = deps.getCustomerEmail();
            const customerPhone = await deps.getCustomerPhone();
            if (customerPhone) {
                body['phone'] = customerPhone;
            }
            const newCustomer = deps.getNewCustomerDraft();
            if (newCustomer.useShippingName) {
                // The shipping snapshot only carries name/company on the
                // content path; a picked-card (ID-only) snapshot or a
                // skipped shipping section contributes nothing, and the
                // server falls back to naming the order by email.
                const shippingAddress = orderShippingOption?.shippingOption?.address;
                body['fullName'] = shippingAddress?.fullName ?? '';
                body['company'] = shippingAddress?.organization ?? '';
            } else {
                body['firstName'] = newCustomer.firstName;
                body['lastName'] = newCustomer.lastName;
                body['company'] = newCustomer.company;
            }
        }
        // Without a resolved customer the preview priced no available discount,
        // so the order spends none either (the server would otherwise resolve
        // the email and spend that customer's whole balance). Otherwise the
        // choice rides as a decimal string, like the preview's param; omitted
        // means spend all available, the server default.
        const availableDiscountChoice = userID ? priced.availableDiscountChoice : Big(0);
        if (availableDiscountChoice !== undefined) {
            body['applyAvailableDiscount'] = availableDiscountChoice.toFixed(2);
        }
        const override = deps.getShippingOverride();
        if (override !== undefined) {
            // Decimal-as-string keeps shopspring's UnmarshalJSON on the lossy
            // side of JSON numbers — matches the cart preview's toFixed(2)
            // shippingOverride param.
            body['shippingOverride'] = override.toFixed(2);
        }
        const lineItemOverrides = deps.getLineItemOverrides();
        if (lineItemOverrides.length > 0) {
            body['lineItemOverrides'] = lineItemOverrides;
        }
        const eventDate = deps.getEventDate();
        if (eventDate) {
            // yyyy-mm-dd from the HTML5 date input maps straight onto
            // smg.Order.EventDate's `datetime=2006-01-02` binding. Skip the
            // field entirely when empty so the omitzero column default holds.
            body['eventDate'] = eventDate;
        }
        if (deps.getSMSOptIn()) {
            // Only emit when checked — server treats unset/false as a no-op so
            // unchecked submits don't touch the customer's flag state.
            body['smsOptIn'] = true;
        }
        if (deps.getPromotionalApproval()) {
            body['promotionalApproval'] = true;
        }
        const notes = deps.getNotes();
        if (notes) {
            body['notes'] = notes;
        }
        const promotionalCodeID = deps.getPromotionalCode();
        if (promotionalCodeID) {
            // Only emit when a code is selected — the server treats an empty/
            // absent value as "no promo" and the order carries no discount line.
            body['promotionalCodeID'] = promotionalCodeID;
        }
        const skipDepositOrderIDs = deps.getSkipDepositOrderIDs();
        if (skipDepositOrderIDs.length > 0) {
            // Only emit when the admin unchecked at least one deposit — absent
            // means apply everything, today's default behavior.
            body['skipDepositOrderIDs'] = skipDepositOrderIDs;
        }
        if (orderShippingOption) {
            // Sent as an array — the server's OrderShippingOption nested-CRUD
            // path expects a slice, and the legacy customer-api also keys
            // shipping by an array of IDs.
            body['orderShippingOptions'] = [orderShippingOption];
        }
        const userTaxExemptionOrder = await deps.getUserTaxExemptionOrder();
        if (userTaxExemptionOrder) {
            // Sent as an array — Order.AfterNew bulk-inserts UserTaxExemptionOrder
            // rows back-stamped to the new order, same nested-CRUD shape as
            // orderShippingOptions.
            body['userTaxExemptionOrders'] = [userTaxExemptionOrder];
        }
        return axios.post('/v2/orders', body).then(r => models.OrderCreateResponse.fromJSON(r.data).order);
    }

    // Charge sequence: account balance first (if applied), then the selected
    // payment method. Re-entered after a balance retry — balancePending gates
    // the balance step so a card retry never re-charges it.
    function runCharges(order: models.Order): void {
        const balance = deps.getBalanceDraft();
        if (balancePending && balance.useBalance && balance.applied.gt(0)) {
            void chargeBalance(order, balance.applied);
            return;
        }
        balancePending = false;
        startMethodCharge(order);
    }

    async function chargeBalance(order: models.Order, applied: InstanceType<typeof Big>): Promise<void> {
        setProcessing('Applying balance…');
        try {
            balanceIdemKey ??= crypto.randomUUID();
            const result = await ChargeOrderPayment({
                orderID: order.id,
                total: applied,
                // Inline balance option — the server forces its owner/brand to
                // the order's and debits the customer's stored balance.
                paymentOption: { paymentOptionTypeID: models.PaymentOptionTypeID.PAYMENT_OPTION_TYPE_BALANCE },
            }, undefined, balanceIdemKey);
            if (result.declined) {
                handleBalanceFailure(order, result.declineMessage || 'Account balance was declined.');
                return;
            }
            balancePending = false;
            runCharges(order);
        } catch (err) {
            ShowError(err);
            // Lost response is safe to retry: balanceIdemKey is reused and the
            // server replays a committed balance debit (#5656).
            // Keep the amount locked for this ambiguous path: if the first
            // request committed, changing it before replay would make the
            // displayed post-balance card total disagree with the actual debit.
            handleBalanceRetry(order);
        }
    }

    function startMethodCharge(order: models.Order): void {
        setProcessing('Processing…');
        deps.chargePayment({
            orderID: order.id,
            companyID: deps.getCompanyID(),
            onComplete: () => void completeCheckout(order),
            onDecline: decline => handleCardDecline(order, decline),
            onError: err => handleChargeError(order, err),
        });
    }

    // Balance decline (insufficient credit) — re-enable the balance section so
    // the admin can lower the amount or switch to "No", then retry. Balance has
    // no cross-origin iframe, so the Retry button can be enabled immediately.
    function handleBalanceFailure(order: models.Order, message: string): void {
        showDecline(message, order);
        setBalanceEditable(true);
        setLabel('Retry payment');
        $btn.disabled = false;
        retryAction = () => {
            retryAction = undefined;
            hideDecline();
            setBalanceEditable(false);
            // balancePending stays true → balance recharges, then the method.
            runCharges(order);
        };
    }

    function handleBalanceRetry(order: models.Order): void {
        showDecline(i18n('balanceRetryAmbiguous'), order);
        setLabel('Retry payment');
        $btn.disabled = false;
        retryAction = () => {
            retryAction = undefined;
            hideDecline();
            runCharges(order);
        };
    }

    // Card decline — payment.ts has unlocked every not-yet-charged card (and
    // the Add card button) so the admin can correct, split, or remove. The
    // Retry button is enabled immediately; clicking it re-charges whatever's
    // still unprocessed.
    function handleCardDecline(order: models.Order, decline: PaymentDecline): void {
        showDecline(decline.message, order);
        setLabel('Retry charge');
        $btn.disabled = false;
        retryAction = () => {
            retryAction = undefined;
            hideDecline();
            retryAfterPurchaseOrderCheck(order, () => handleCardDecline(order, decline), () => {
                setProcessing('Processing…');
                decline.retry();
            });
        };
    }

    // Hard, non-card failure (manual PO/check/wire charge, or split-amount
    // resolution). The order exists, so offer a plain retry of the method
    // charge rather than a re-submit — the field values persist while disabled.
    function handleChargeError(order: models.Order, err: unknown): void {
        ShowError(err);
        offerChargeRetry(order);
    }

    function offerChargeRetry(order: models.Order): void {
        // Manual (PO/check/wire) retries reuse the type's idempotency key in
        // payment.ts, so a lost response cannot record a duplicate (#5656).
        showDecline('Payment couldn’t be completed. Review the details and retry.', order);
        setLabel('Retry payment');
        $btn.disabled = false;
        retryAction = () => {
            retryAction = undefined;
            hideDecline();
            retryAfterPurchaseOrderCheck(order, () => offerChargeRetry(order), () => startMethodCharge(order));
        };
    }

    // Fires the customer's order confirmation, as the brand checkouts do once
    // their charges finish. The order and its payments already exist, so a
    // failure retries only this call.
    async function completeCheckout(order: models.Order): Promise<void> {
        setProcessing(i18n('finishingCheckout'));
        try {
            await axios.post(`/v2/orders/${order.id}/checkout-complete`);
        } catch (err) {
            ShowError(err);
            showDecline(i18n('checkoutCompleteFailed'), order);
            setLabel(i18n('retry'));
            $btn.disabled = false;
            retryAction = () => {
                retryAction = undefined;
                hideDecline();
                void completeCheckout(order);
            };
            return;
        }
        redirect(order);
    }

    function redirect(order: models.Order): void {
        window.location.href = accounts.WithProfile(order.iapURL());
    }

    function showDecline(message: string, order: models.Order): void {
        $declineMessage.textContent = message;
        // The order is already created — link to it (by invoice number) so the
        // admin can bail to the order page and finish payment there if they
        // don't retry here.
        $declineOrderLink.href = order.iapURL();
        $declineOrderLinkText.textContent = i18n('viewOrderN').replace('%s', String(order.invoiceNumber ?? ''));
        $declineAlert.classList.remove('d-none');
    }

    function hideDecline(): void {
        $declineAlert.classList.add('d-none');
    }

    // Re-enable just the balance section's controls (the form-wide disable
    // covers them) so the admin can correct a declined balance and retry.
    function setBalanceEditable(editable: boolean): void {
        for (const id of ['balance-use-yes', 'balance-use-no', 'balance-input', 'balance-apply', 'balance-clear']) {
            const $el = document.getElementById(id) as HTMLInputElement | HTMLButtonElement | null;
            if ($el) {
                $el.disabled = !editable;
            }
        }
    }

    function disableForm(): void {
        disabledByMe = [];
        $form.querySelectorAll<HTMLInputElement | HTMLSelectElement | HTMLTextAreaElement | HTMLButtonElement>(
            'input, select, textarea, button',
        ).forEach($el => {
            // Only track controls we actually disable so reEnableForm doesn't
            // un-disable inputs payment.ts intentionally gated off (inactive
            // payment-type fieldsets, the skip toggle, hidden billing fields).
            if (!$el.disabled) {
                $el.disabled = true;
                disabledByMe.push($el);
            }
        });

        // Upload dropzones are <label>s and their delete controls are <a>s —
        // neither is a form control, so `disabled` can't reach them. Mirror the
        // card-iframe freeze: dim + block pointer events so they read as locked.
        neutralizedByMe = [];
        $form.querySelectorAll<HTMLInputElement>('input[type="file"]').forEach($file => {
            const $label = $file.closest('label');
            if ($label) {
                $label.style.pointerEvents = 'none';
                $label.style.opacity = '0.65';
                neutralizedByMe.push($label);
            }
        });
        $form.querySelectorAll<HTMLElement>('.uploader-thumbnails').forEach($thumbnails => {
            $thumbnails.style.pointerEvents = 'none';
            $thumbnails.style.opacity = '0.65';
            neutralizedByMe.push($thumbnails);
        });

        // Saved-option cards are <div role="button">s and summary rows re-render
        // when a pending load lands; picking a card or editing a line mid-submit
        // would change what the order was captured with. inert blocks pointer
        // and keyboard for everything inside, now or rendered later.
        inertByMe = [];
        for (const $container of $rerenderedContainers) {
            if (!$container.inert) {
                $container.inert = true;
                inertByMe.push($container);
            }
        }
    }

    function reEnableForm(): void {
        for (const $el of disabledByMe) {
            $el.disabled = false;
        }
        disabledByMe = [];
        for (const $el of neutralizedByMe) {
            $el.style.pointerEvents = '';
            $el.style.opacity = '';
        }
        neutralizedByMe = [];
        for (const $el of inertByMe) {
            $el.inert = false;
        }
        inertByMe = [];
    }

    function setProcessing(text: string): void {
        $btn.disabled = true;
        $btn.classList.add('disabled');
        $btn.innerHTML = `<span class="spinner-border spinner-border-sm me-2" role="status" aria-hidden="true"></span>${text}`;
    }

    function setLabel(text: string): void {
        $btn.classList.remove('disabled');
        $btn.textContent = text;
    }

    return { refresh };
}
